import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Gavel, Menu as MenuIcon } from "@mui/icons-material";
import { Box, Container, IconButton, LinearProgress, ThemeProvider, } from "@mui/material";
import { clubTheme, isCurrentOrigin, RegLogo, regTheme, S4HorseLogo, SISAppleLogo, s4Theme, sisTheme, theme, useEnv, Y2cLogo, } from "@ssf/components";
import { useIsFetching } from "@tanstack/react-query";
import { useCallback, useLayoutEffect, useRef, useState, } from "react";
import { useTranslation } from "react-i18next";
import { pagePermissions } from "../../lib";
import { useAuth } from "../AuthProvider";
import { DesktopNavbar } from "./DesktopNavbar";
import { MobileNavbar } from "./MobileNavbar";
export const Navbar = ({ renderHeader, showAllLinks, children, }) => {
    const { user, isInitiated } = useAuth();
    const isFetching = useIsFetching();
    const env = useEnv();
    const { t } = useTranslation("Navbar");
    const [mobileOpen, setMobileOpen] = useState(false);
    const closeMobileMenu = useCallback(() => setMobileOpen(false), []);
    const openMobileMenu = useCallback(() => setMobileOpen(true), []);
    const navItems = [
        {
            key: "club",
            theme: clubTheme,
            to: env.VITE_CLUB_WEB_URL,
            icon: _jsx(Gavel, {}),
            label: t("club"),
            active: isCurrentOrigin(env.VITE_CLUB_WEB_URL),
            svgHeight: 23,
            hasPermission: isInitiated && pagePermissions.clubWeb(user),
        },
        {
            key: "s4",
            theme: s4Theme,
            to: env.VITE_S4_WEB_URL,
            icon: _jsx(S4HorseLogo, {}),
            label: t("s4"),
            active: isCurrentOrigin(env.VITE_S4_WEB_URL),
            svgHeight: 23,
            hasPermission: isInitiated && pagePermissions.s4(user),
        },
        {
            key: "y2c",
            theme: sisTheme,
            to: env.VITE_Y2C_WEB_URL,
            icon: _jsx(Y2cLogo, {}),
            label: t("y2c"),
            active: isCurrentOrigin(env.VITE_Y2C_WEB_URL),
            svgHeight: 23,
            hasPermission: isInitiated && pagePermissions.y2c(user),
        },
        {
            key: "reg",
            theme: regTheme,
            to: env.VITE_REG_WEB_URL,
            icon: _jsx(RegLogo, {}),
            label: t("reg"),
            active: isCurrentOrigin(env.VITE_REG_WEB_URL),
            svgHeight: 23,
            hasPermission: isInitiated && pagePermissions.reg(user),
        },
    ];
    const visibleNavItems = navItems.filter((item) => showAllLinks || (isInitiated && (item.active || item.hasPermission)));
    const homeItem = {
        key: "sis",
        theme: sisTheme,
        to: env.VITE_PORTAL_WEB_URL,
        icon: _jsx(SISAppleLogo, {}),
        label: t("title"),
        active: isCurrentOrigin(env.VITE_PORTAL_WEB_URL),
    };
    const activeItem = visibleNavItems.find((item) => item.active) ?? homeItem;
    const { collapsed, measurementRefs, childrenMeasureRef } = useCollapseOnNavbarOverflow(closeMobileMenu);
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: {
                    position: "sticky",
                    top: 0,
                    zIndex: 100,
                }, children: [_jsx(LinearProgress, { value: 100, variant: isFetching ? "indeterminate" : "determinate", sx: {
                            "> span": { background: ({ palette }) => palette.primary.main },
                        } }), _jsx(DesktopNavbar, { homeItem: homeItem, collapsed: collapsed, items: visibleNavItems, measurementRefs: measurementRefs, children: _jsxs(Box, { sx: {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                alignSelf: "stretch",
                                gap: 1,
                            }, children: [_jsx(Box, { ref: childrenMeasureRef, sx: { display: "flex", alignItems: "center", minWidth: 0 }, children: children }), _jsx(Box, { sx: { display: collapsed ? "inline-flex" : "none" }, children: _jsx(ThemeProvider, { theme: activeItem.theme, children: _jsx(IconButton, { "aria-label": t("openMobileMenu"), onClick: openMobileMenu, sx: { color: (theme) => theme.palette.primary.main }, children: _jsx(MenuIcon, {}) }) }) })] }) })] }), _jsx(MobileNavbar, { open: mobileOpen, onClose: closeMobileMenu, homeItem: homeItem, items: visibleNavItems }), renderHeader && (_jsx(Container, { maxWidth: "lg", disableGutters: true, children: renderHeader({ collapsed }) }))] }));
};
const useCollapseOnNavbarOverflow = (onExpand) => {
    const [collapsed, setCollapsed] = useState(() => window.innerWidth < theme.breakpoints.values.md);
    const stackMeasureRef = useRef(null);
    const homeMeasureRef = useRef(null);
    const navMeasureRef = useRef(null);
    const childrenMeasureRef = useRef(null);
    useLayoutEffect(() => {
        let animationFrame = 0;
        const updateCollapsed = () => {
            cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(() => {
                const navbar = stackMeasureRef.current;
                const home = homeMeasureRef.current;
                const nav = navMeasureRef.current;
                const children = childrenMeasureRef.current;
                if (!navbar || !home || !nav || !children)
                    return;
                const availableWidth = navbar.clientWidth;
                const homeWidth = home.scrollWidth;
                const navWidth = nav.scrollWidth;
                const childrenWidth = children.scrollWidth;
                // The nav links are centered within the bar, so the space on each side
                // (home logo on the left, children/actions on the right) must be equal
                // to keep them centered. We reserve the wider of the two sides on both
                // sides (`sideWidth * 2`) and add the nav's own width to get the total
                // width the expanded layout needs. If that exceeds the available width,
                // we collapse into the mobile menu.
                const sideWidth = Math.max(homeWidth, childrenWidth);
                const requiredWidth = navWidth + sideWidth * 2;
                const nextCollapsed = requiredWidth > availableWidth;
                setCollapsed(nextCollapsed);
                if (!nextCollapsed)
                    onExpand();
            });
        };
        updateCollapsed();
        const resizeObserver = new ResizeObserver(updateCollapsed);
        for (const element of [
            stackMeasureRef.current,
            homeMeasureRef.current,
            navMeasureRef.current,
            childrenMeasureRef.current,
        ]) {
            if (element)
                resizeObserver?.observe(element);
        }
        window.addEventListener("resize", updateCollapsed);
        document.fonts?.ready.then(updateCollapsed);
        return () => {
            cancelAnimationFrame(animationFrame);
            resizeObserver?.disconnect();
            window.removeEventListener("resize", updateCollapsed);
        };
    }, [onExpand]);
    return {
        collapsed,
        childrenMeasureRef,
        measurementRefs: {
            stackMeasureRef,
            homeMeasureRef,
            navMeasureRef,
        },
    };
};
//# sourceMappingURL=Navbar.js.map